/**
 * useShareModal - Shared hook for entity share modal logic
 *
 * Encapsulates the common pattern of fetching organization users, filtering
 * them by search text, managing multi-select state, and executing a share
 * function for each selected user. Both report and note sharing containers
 * delegate to this hook to avoid duplicating the same stateful logic.
 */

import { useCallback, useMemo, useState } from "react";

import { useListKanbanOrganizationUsersQuery } from "@/generated/graphql";
import { useToastNotification } from "@/hooks/useToastNotification";
import { useTheme } from "@/providers/ThemeProvider";
import { formatError } from "@/utils/formatError";

/** Represents a Cognito user attribute with Name and Value properties */
type CognitoAttribute = { readonly Name: string; readonly Value: string };

/** Display data for an available organization user in the share modal */
export interface AvailableUserDisplayData {
  readonly userId: string;
  readonly displayName: string;
  readonly initials: string;
  readonly title: string;
  readonly orgRoleName?: string;
}

/** Options for configuring the share modal hook */
export interface UseShareModalOptions {
  /** Whether the share modal is currently open */
  readonly isOpen: boolean;
  /** Callback to close the modal */
  readonly onClose: () => void;
  /** ID of the entity being shared (report ID, note ID, etc.) */
  readonly entityId: string;
  /** Function that performs the share mutation for a single target user */
  readonly shareFn: (targetUserId: string) => Promise<unknown>;
  /** Human-readable label for the entity type, used in toast messages */
  readonly entityLabel: string;
  /** User IDs to exclude from the available users list (e.g. already-shared users) */
  readonly excludeUserIds?: readonly string[];
  /** Org roles to keep visible in the available list even when in excludeUserIds (e.g. ["MANAGER", "ADMIN"]) */
  readonly keepOrgRoles?: readonly string[];
}

/** Return value from useShareModal containing all state and handlers for the view */
export interface UseShareModalReturn {
  readonly isOpen: boolean;
  readonly availableUsers: readonly AvailableUserDisplayData[];
  /** All organization users (unfiltered by excludeUserIds or search) */
  readonly allOrgUsers: readonly AvailableUserDisplayData[];
  readonly selectedUserIds: readonly string[];
  /** User IDs that are pre-selected because they already have access via keepOrgRoles */
  readonly preSelectedUserIds: readonly string[];
  readonly allSelected: boolean;
  readonly loading: boolean;
  readonly isSharing: boolean;
  readonly searchText: string;
  readonly isDark: boolean;
  readonly onToggleUser: (userId: string) => void;
  readonly onToggleAll: () => void;
  readonly onShare: () => void;
  readonly onCancel: () => void;
  readonly onSearchChange: (text: string) => void;
}

/**
 * Gets Cognito attributes from a user, preferring Attributes over UserAttributes
 * because the attribute location depends on how the user was fetched from Cognito
 * @param cognitoUser - The Cognito user object
 * @returns Array of Cognito attributes
 */
const getCognitoAttributes = (
  cognitoUser?: {
    readonly Attributes?: readonly CognitoAttribute[] | null;
    readonly UserAttributes?: readonly CognitoAttribute[] | null;
  } | null
): readonly CognitoAttribute[] =>
  cognitoUser?.Attributes ?? cognitoUser?.UserAttributes ?? [];

/**
 * Finds an attribute value by name from a list of Cognito attributes
 * @param attributes - The array of Cognito attributes
 * @param name - The attribute name to find
 * @returns The attribute value or empty string if not found
 */
const getAttributeValue = (
  attributes: readonly CognitoAttribute[],
  name: string
): string => attributes.find(attr => attr.Name === name)?.Value ?? "";

/**
 * Extracts a human-readable display name from a Cognito user by checking
 * given_name + family_name first, falling back to email, then Username
 * @param cognitoUser - The Cognito user object
 * @returns The best available display name string
 */
const getDisplayName = (
  cognitoUser?: {
    readonly Attributes?: readonly CognitoAttribute[] | null;
    readonly UserAttributes?: readonly CognitoAttribute[] | null;
    readonly Username?: string | null;
  } | null
): string => {
  const attributes = getCognitoAttributes(cognitoUser);
  const givenName = getAttributeValue(attributes, "given_name");
  const familyName = getAttributeValue(attributes, "family_name");
  const email = getAttributeValue(attributes, "email");

  const fullName = `${givenName} ${familyName}`.trim();
  if (fullName) return fullName;
  if (email) return email;
  return cognitoUser?.Username ?? "User";
};

/**
 * Extracts initials from a display name (first letter of first + last name)
 * for use in avatar circles when no profile image is available
 * @param displayName - The full display name
 * @returns Uppercase initials string (1-2 characters)
 */
const getInitials = (displayName: string): string => {
  const parts = displayName.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return `${first}${last}`.toUpperCase();
};

/**
 * Shared hook that manages the complete share modal lifecycle: fetching
 * organization users, filtering by search, multi-select toggling, and
 * executing the share function for each selected user with toast feedback.
 * @param options - Configuration for the share modal behavior
 * @param options.isOpen - Whether the share modal is currently open
 * @param options.onClose - Callback to close the modal
 * @param options.entityId - ID of the entity being shared
 * @param options.shareFn - Function that performs the share mutation for a single target user
 * @param options.entityLabel - Human-readable label for toast messages (e.g. "Report")
 * @param options.excludeUserIds - User IDs to omit from the available users list
 * @param options.keepOrgRoles - Org roles that bypass excludeUserIds filtering and appear pre-selected
 * @returns All state and handlers needed to render the ShareModalView
 */
export const useShareModal = ({
  isOpen,
  onClose,
  entityId,
  shareFn,
  entityLabel,
  excludeUserIds,
  keepOrgRoles,
}: UseShareModalOptions): UseShareModalReturn => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const toast = useToastNotification();
  const [selectedUserIds, setSelectedUserIds] = useState<readonly string[]>([]);
  const [searchText, setSearchText] = useState("");
  const [isSharing, setIsSharing] = useState(false);

  const { data: usersData, loading } = useListKanbanOrganizationUsersQuery({
    variables: { first: 500 },
    skip: !isOpen,
  });

  const allOrgUsers = useMemo(
    () =>
      usersData?.listMyOrganizationsOrganizationUsers?.edges
        ?.map(edge => edge.node)
        .filter(orgUser => !!orgUser.user)
        .map(orgUser => ({
          userId: orgUser.user?.id ?? "",
          displayName: getDisplayName(orgUser.user?.cognitoUser),
          initials: getInitials(getDisplayName(orgUser.user?.cognitoUser)),
          title: orgUser.user?.title ?? "",
          orgRoleName: orgUser.role?.name ?? undefined,
        })) ?? [],
    [usersData]
  );

  const keepRolesSet = useMemo(
    () => new Set(keepOrgRoles ?? []),
    [keepOrgRoles]
  );

  const availableUsers = useMemo(() => {
    const excludeSet = new Set(excludeUserIds ?? []);
    const users = allOrgUsers.filter(
      user =>
        !excludeSet.has(user.userId) ||
        (user.orgRoleName && keepRolesSet.has(user.orgRoleName))
    );

    if (!searchText.trim()) return users;

    const searchLower = searchText.toLowerCase().trim();
    return users.filter(
      user =>
        user.displayName.toLowerCase().includes(searchLower) ||
        user.title.toLowerCase().includes(searchLower)
    );
  }, [allOrgUsers, searchText, excludeUserIds, keepRolesSet]);

  /** User IDs that are already shared via keepOrgRoles and should appear pre-selected */
  const preSelectedUserIds = useMemo(() => {
    const excludeSet = new Set(excludeUserIds ?? []);
    return allOrgUsers
      .filter(
        user =>
          excludeSet.has(user.userId) &&
          user.orgRoleName &&
          keepRolesSet.has(user.orgRoleName)
      )
      .map(user => user.userId);
  }, [allOrgUsers, excludeUserIds, keepRolesSet]);

  const preSelectedSet = useMemo(
    () => new Set(preSelectedUserIds),
    [preSelectedUserIds]
  );

  /** Combined selected IDs: user selections + pre-selected (already-shared) org role users */
  const combinedSelectedUserIds = useMemo(
    () => [...new Set([...selectedUserIds, ...preSelectedUserIds])],
    [selectedUserIds, preSelectedUserIds]
  );

  const allSelected = useMemo(
    () =>
      availableUsers.length > 0 &&
      availableUsers.every(user =>
        combinedSelectedUserIds.includes(user.userId)
      ),
    [availableUsers, combinedSelectedUserIds]
  );

  const handleToggleUser = useCallback(
    (userId: string) => {
      if (preSelectedSet.has(userId)) return;
      setSelectedUserIds(prev =>
        prev.includes(userId)
          ? prev.filter(id => id !== userId)
          : [...prev, userId]
      );
    },
    [preSelectedSet]
  );

  const handleToggleAll = useCallback(() => {
    const nonPreSelectedUsers = availableUsers.filter(
      u => !preSelectedSet.has(u.userId)
    );
    setSelectedUserIds(prev =>
      prev.length === nonPreSelectedUsers.length
        ? []
        : nonPreSelectedUsers.map(u => u.userId)
    );
  }, [availableUsers, preSelectedSet]);

  const handleCancel = useCallback(() => {
    setSelectedUserIds([]);
    setSearchText("");
    onClose();
  }, [onClose]);

  const handleShare = useCallback(async () => {
    const newUserIds = selectedUserIds.filter(id => !preSelectedSet.has(id));
    if (newUserIds.length === 0 || !entityId) return;

    const entityLower = entityLabel.toLowerCase();
    setIsSharing(true);
    try {
      await Promise.all(newUserIds.map(targetUserId => shareFn(targetUserId)));
      const count = newUserIds.length;
      toast.show({
        description: `${entityLabel} shared with ${count} teammate${count > 1 ? "s" : ""}`,
        action: "success",
      });
      setSelectedUserIds([]);
      setSearchText("");
      onClose();
    } catch (error) {
      toast.show({
        description:
          formatError(error)?.message ?? `Failed to share ${entityLower}`,
        action: "danger",
      });
    } finally {
      setIsSharing(false);
    }
  }, [
    selectedUserIds,
    preSelectedSet,
    entityId,
    shareFn,
    entityLabel,
    toast,
    onClose,
  ]);

  return {
    isOpen,
    availableUsers,
    allOrgUsers,
    selectedUserIds: combinedSelectedUserIds,
    preSelectedUserIds,
    allSelected,
    loading,
    isSharing,
    searchText,
    isDark,
    onToggleUser: handleToggleUser,
    onToggleAll: handleToggleAll,
    onShare: handleShare,
    onCancel: handleCancel,
    onSearchChange: setSearchText,
  };
};
